/**
 * DÍA 4 — Retrieval + Correcciones.
 *
 * Convierte lenguaje reconocido en lenguaje que aparece rápido cuando hace
 * falta. El material sale del Learning Bank: errores, expresiones, situaciones
 * de trabajo, problemas de listening, feedback de ChatGPT y cosas que se
 * quisieron decir y no se pudo.
 *
 * REGLA DURA: no mostrar primero la respuesta correcta.
 * Flujo: situación → intento de recuperar → circumlocution en inglés →
 * ayuda si hace falta → uso en conversación → transferencia a contexto nuevo.
 *
 * Arranque en frío: con menos de 3 elementos activos, la sesión lo dice y usa
 * el material del baseline más captura en vivo (CA-5.11).
 */

import type { Sesion } from '../../tipos.js';

/** Qué hacer cuando el banco todavía no tiene material suficiente. */
const BANCO_INSUFICIENTE =
  'Si tenés menos de 3 elementos activos en el Learning Bank, no pasa nada y no hay que forzarlo: usá las dificultades que guardaste en el baseline y, durante la conversación, andá capturando lo que te falte. En la Semana 1 esto es lo normal.';

export const D4: Sesion = {
  clave: 'd4',
  tipo: 'dia',
  etiqueta: 'Día 4 · Retrieval + Correcciones',
  titulo: 'Día 4 · Recuperar sin mirar',
  corto: 'Retrieval',
  objetivo:
    'Convertir lenguaje que reconocés en lenguaje que aparece rápido cuando lo necesitás.',
  porQue:
    'Reconocer una expresión no garantiza poder producirla a tiempo en una conversación. Son dos habilidades distintas, y la que falla en la vida real es la segunda.',
  areasAEvaluar: ['recuperacion_lexica', 'naturalidad', 'gramatica_fluidez'],
  capturaCorrecciones: true,
  planes: {
    20: {
      duracion: 20,
      pasos: [
        {
          clave: 'd4-seleccion',
          titulo: 'Seleccionar material',
          minutosSugeridos: 7,
          herramienta: 'ninguna',
          objetivo: 'Elegir entre 3 y 5 elementos activos para trabajar hoy.',
          elementosBanco: { minimo: 3, maximo: 5 },
          porQue:
            'Trabajamos tus errores y expresiones reales, no una lista genérica de vocabulario que no te hace falta.',
          instrucciones: [
            'Abrí el Learning Bank y filtrá por activos.',
            'Elegí entre 3 y 5 elementos que todavía no te salgan automáticos.',
            'No leas las respuestas ahora: las vas a necesitar ocultas en el paso siguiente.',
            BANCO_INSUFICIENTE,
          ],
          areas: ['recuperacion_lexica'],
        },
        {
          clave: 'd4-retrieval',
          titulo: 'Retrieval por situaciones',
          minutosSugeridos: 13,
          herramienta: 'chatgpt',
          prompt: 'd4',
          objetivo: 'Producir desde el significado hacia el inglés, sin pistas.',
          porQue:
            'La situación te obliga a recuperar. Releer la respuesta solo entrena reconocimiento, que ya tenés.',
          instrucciones: [
            'Copiá el prompt y entrá a Voice.',
            'Si no recordás algo, rodealo en inglés antes de pedir ayuda.',
            'Después usá ese lenguaje en conversación, no lo dejes en el ejercicio.',
          ],
          uso: {
            queHacer:
              'Pegá el prompt en ChatGPT, entrá en Voice y dejá que te dé situaciones que te obliguen a recuperar tu material. Circumlocution antes que ayuda.',
          },
          areas: ['recuperacion_lexica', 'naturalidad'],
        },
      ],
    },

    35: {
      duracion: 35,
      pasos: [
        {
          clave: 'd4-retrieval-previo',
          titulo: 'Recuperación espaciada',
          minutosSugeridos: 10,
          herramienta: 'ninguna',
          objetivo: 'Recuperar material previo sin mirar la respuesta.',
          elementosBanco: { minimo: 3, maximo: 6 },
          porQue:
            'El acceso rápido exige volver a producir después de un intervalo. Si solo lo repasás el mismo día, no se consolida.',
          instrucciones: [
            'Seleccioná entre 3 y 6 elementos activos.',
            'Intentá producirlos partiendo de la situación, sin leer la respuesta.',
            'Marcá cuáles salieron solos y cuáles no.',
            BANCO_INSUFICIENTE,
          ],
          areas: ['recuperacion_lexica'],
        },
        {
          clave: 'd4-conversacion',
          titulo: 'Conversación con el material recuperado',
          minutosSugeridos: 20,
          herramienta: 'chatgpt',
          prompt: 'd4',
          objetivo: 'Usarlo sin guion, en una conversación real.',
          porQue:
            'El contexto cambia para que la expresión siga siendo flexible en vez de convertirse en una frase hecha.',
          instrucciones: [
            'Copiá el prompt y entrá a Voice.',
            'No fuerces todos los elementos: usá los que encajen naturalmente.',
            'Si uno no aparece nunca, quizá no era tan útil como creías.',
          ],
          uso: {
            queHacer:
              'Pegá el prompt en ChatGPT, entrá en Voice y conversá usando el material que acabás de recuperar, sin forzarlo.',
          },
          areas: ['recuperacion_lexica', 'naturalidad', 'habla_espontanea'],
        },
        {
          clave: 'd4-dominio',
          titulo: 'Marcar dominio',
          minutosSugeridos: 5,
          herramienta: 'ninguna',
          esReflexion: true,
          notaTiempo: 'Si terminás antes, avanzá. No hace falta agotar los 5 minutos.',
          objetivo: 'Retirar de la vista activa lo que ya sale fácil.',
          porQue:
            'El banco debe concentrarse en lo que todavía aporta. Lo dominado no se borra: sale de la vista y queda en el historial.',
          instrucciones: [
            'Marcá como dominado lo que hoy recuperaste sin esfuerzo.',
            'Lo que te costó, dejalo activo y subile la prioridad si hace falta.',
          ],
        },
      ],
    },

    50: {
      duracion: 50,
      pasos: [
        {
          clave: 'd4-retrieval-previo',
          titulo: 'Recuperación espaciada',
          minutosSugeridos: 12,
          herramienta: 'ninguna',
          objetivo: 'Recuperar sin pistas visibles.',
          elementosBanco: { minimo: 5, maximo: 8 },
          porQue: 'Fortalece el camino situación → inglés, que es el que usás al hablar.',
          instrucciones: [
            'Elegí entre 5 y 8 elementos activos.',
            'Producí ejemplos NUEVOS con cada uno, no el ejemplo que guardaste.',
            BANCO_INSUFICIENTE,
          ],
          areas: ['recuperacion_lexica'],
        },
        {
          clave: 'd4-conversacion',
          titulo: 'Recuperar, conversar y transferir',
          minutosSugeridos: 28,
          herramienta: 'chatgpt',
          prompt: 'd4',
          objetivo: 'Llevar material antiguo a una interacción nueva.',
          porQue:
            'El aprendizaje cuenta cuando sobrevive al cambio de contexto. Si solo funciona donde lo aprendiste, no lo aprendiste.',
          instrucciones: [
            'Copiá el prompt y entrá a Voice.',
            'Usá circumlocution antes de pedir ayuda.',
            'Cambiá de contexto varias veces durante la conversación.',
          ],
          uso: {
            queHacer:
              'Pegá el prompt en ChatGPT, entrá en Voice, recuperá tu material en situaciones y después llevalo a contextos distintos.',
          },
          areas: ['recuperacion_lexica', 'naturalidad', 'habla_espontanea'],
        },
        {
          clave: 'd4-limpieza',
          titulo: 'Limpiar el banco',
          minutosSugeridos: 10,
          herramienta: 'ninguna',
          esReflexion: true,
          notaTiempo: 'Si terminás antes, avanzá. No hace falta agotar los 10 minutos.',
          objetivo: 'Mantener el sistema ligero.',
          porQue:
            'Demasiados elementos crean fricción y repaso superficial. Un banco chico y vivo vale más que uno grande y muerto.',
          instrucciones: [
            'Marcá como dominado lo que ya recuperás con facilidad.',
            'Archivá lo que resultó no ser útil.',
            'Conservá solo material recurrente y de alto impacto.',
            'Nada se borra: lo dominado y lo archivado siguen en el historial.',
          ],
        },
      ],
    },
  },
};
